/**
 * Wire contract for the admin appeal ACTION surfaces (AUT-130 / ADMIN-18 —
 * the SPA bind target for the pre-existing AUT-105 actions):
 *
 *   - `POST /admin/appeals/:id/review`   → empty body (review_start)
 *   - `POST /admin/appeals/:id/resolve`  → `ResolveAppealBody`
 *
 * Centralised in `@carcheck/shared` so the api Zod schema
 * (`packages/api/src/modules/appeals/dto/resolve-appeal.dto.ts`,
 * `ResolveAppealSchema`) and the admin SPA's RHF+Zod form import the same
 * source of truth — no api↔admin mirror drift. The SPA's resolve form
 * binds its outcome / penalty pickers to the consts here, NEVER to
 * hand-typed string unions (the same pattern `admin-appeal-list.ts` uses
 * for `ADMIN_APPEAL_STATUSES`).
 *
 * Step-up discipline (architect 🟠 — AUT-121):
 *   - `review` (review_start) is a PLAIN mutation — NO step-up.
 *   - `resolve` is the terminal, money-moving (refund) + penalty-applying
 *     action and is gated by a fresh TOTP step-up token (`x-totp-step-up`
 *     header) + the `resolve_appeals` permission on the api side.
 */
/**
 * Admin-facing appeal resolution outcome — the FULL 4-value Prisma
 * `AppealOutcome` enum, mirrored here so the SPA's outcome picker imports
 * the union from `@carcheck/shared` instead of `@prisma/client`. The four
 * outcomes are MUTUALLY EXCLUSIVE (the resolve form is outcome-first; the
 * mockup's fused refund/penalty calculator is illustrative only):
 *
 *   - `refund_full`      — customer gets the full order total back;
 *                          inspector payout = 0. `refundAmount` is computed
 *                          server-side from the snapshot (client omits it).
 *   - `refund_partial`   — partial refund; `refundAmount` OR
 *                          `refundPercentage` is REQUIRED (exactly the Zod
 *                          refine — at least one of the two).
 *   - `reject_appeal`    — no money moves; the order un-disputes.
 *   - `inspector_penalty`— a penalty is applied to the inspector;
 *                          `inspectorPenaltyType` is REQUIRED.
 *
 * Single source of truth shared with the api's `ResolveAppealSchema`
 * (`z.nativeEnum(AppealOutcome)`) — the values are 1:1 with the Prisma
 * enum (`schema.prisma`).
 */
export const ADMIN_APPEAL_OUTCOMES = [
    'refund_full',
    'refund_partial',
    'reject_appeal',
    'inspector_penalty',
];
/**
 * Admin-facing inspector-penalty type — the FULL 3-value Prisma
 * `PenaltyType` enum. Only meaningful when `outcome === 'inspector_penalty'`.
 *
 *   - `warning`    — a recorded warning; no duration.
 *   - `suspension` — a temporary suspension; `inspectorPenaltyDays`
 *                    (int 1-365) is the SPA-required duration field.
 *   - `ban`        — a permanent ban; no duration.
 *
 * Single source of truth shared with the api's `ResolveAppealSchema`
 * (`z.nativeEnum(PenaltyType)`).
 */
export const ADMIN_APPEAL_PENALTY_TYPES = ['warning', 'suspension', 'ban'];
/** Exact Zod bounds mirrored from `ResolveAppealSchema` (api). */
export const RESOLVE_APPEAL_BOUNDS = {
    /** `decisionReason` — trimmed min/max. */
    decisionReasonMin: 5,
    decisionReasonMax: 5000,
    /** `refundPercentage` — inclusive range. */
    refundPercentageMin: 0,
    refundPercentageMax: 100,
    /** `refundAmount` — lower bound only (upper = order total, clamped SPA-side). */
    refundAmountMin: 0,
    /** `inspectorPenaltyDays` — inclusive integer range (suspension). */
    inspectorPenaltyDaysMin: 1,
    inspectorPenaltyDaysMax: 365,
    /** `inspectorPenaltyReason` — trimmed max. */
    inspectorPenaltyReasonMax: 2000,
    /** `adminNotes` — trimmed max. */
    adminNotesMax: 5000,
};
//# sourceMappingURL=admin-appeal-actions.js.map